import { DictType } from "@pma/types";
import { useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { SERVER_URL, STATUS_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes";
import useTranslator from "@hooks/use-translator";
import { editProjectByCurrentUser } from "@pma/utils/utils";
import AddStatusForm from "./add-status-form";

interface Props {
  status?: DictType;
  projectId?: number;
  setStatus?: any;
}
const ProjectStatusEditor = ({ status, projectId, setStatus }: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(status?.name);
  const background =
    status?.color === "#transparent" ? undefined : status?.color;
  const label = status?.name;

  const { authConfig } = useAuth();
  const navigate = useNavigate();
  const { translate, locale } = useTranslator();
  const location = useLocation();

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const isHebrew = locale === "he";

  const searchParams = location.search;

  const getStatuses = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${STATUS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}&for_task=false&for_project=true`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data, refetch } = useQuery({
    queryKey: ["project-statuses", orgId],
    queryFn: getStatuses,
  });

  const updateProjectStatusMutation = useMutation({
    mutationKey: ["update-project-status"],
    mutationFn: (statusId: number) =>
      editProjectByCurrentUser({
        project_id: projectId || 0,
        status_id: statusId,
        inform_users: true,
      }),
    onSuccess: () => {
      // Trigger Revalidate project table

      navigate(
        searchParams
          ? `${searchParams}&revalidate=projects`
          : `?revalidate=projects`,
      );
    },
  });

  useEffect(() => {
    status && setValue(status?.name);
  }, [status]);

  if (projectId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="default"
            role="combobox"
            aria-expanded={open}
            className="flex-col items-center text-black md:flex w-fit md:min-w-[80px] h-6 rounded-full text-[11px]"
            style={{
              background,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!value && label && label}
            {value &&
              data?.find((status: DictType) => status?.name === value)?.name}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0">
          <Command className={isHebrew ? "rtl" : "ltr"} onClick={(e) => e.stopPropagation()}>
            <CommandInput placeholder={`${translate("Search")}...`} onClick={(e) => e.stopPropagation()} />
            <CommandList>
              <CommandEmpty>{translate("No status found")}.</CommandEmpty>
              <CommandGroup>
                {data?.map((status: DictType) => (
                  <CommandItem
                    key={status.id}
                    value={status?.name}
                    onSelect={(currentValue: any) => {
                      setValue(status?.name);
                      setOpen(false);
                      updateProjectStatusMutation.mutate(status?.id);
                    }}
                    className={cn(
                      "my-1 text-black",
                      status?.color === "#transparent" && "bg-primary",
                    )}
                    style={{
                      background: status?.color,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {status.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="p-2" onClick={(e) => e.stopPropagation()}>
            <AddStatusForm option="project" refetch={refetch} />
          </div>{" "}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 text-black"
          style={{
            background: data?.find((status: DictType) => status?.name === value)
              ?.color,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {value
            ? data?.find((status: DictType) => status?.name === value)?.name
            : translate("Select Status")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command className={isHebrew ? "rtl" : "ltr"} onClick={(e) => e.stopPropagation()}>
          <CommandInput placeholder={`${translate("Search")}...`} onClick={(e) => e.stopPropagation()} />
          <CommandList>
            <CommandEmpty>{translate("No status found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((status: DictType) => (
                <CommandItem
                  key={status.id}
                  value={status?.name}
                  onSelect={(currentValue: any) => {
                    setValue(status?.name);
                    setOpen(false);
                    setStatus && setStatus(status);
                  }}
                  className={cn(
                    "my-1 text-white",
                    status?.color === "#transparent" && "bg-primary",
                  )}
                  style={{
                    background: status?.color,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {status.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="p-2" onClick={(e) => e.stopPropagation()}>
          <AddStatusForm option="project" refetch={refetch} />
        </div>{" "}
      </PopoverContent>
    </Popover>
  );
};

export default ProjectStatusEditor;
